import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardAppShell from '../components/DashboardAppShell';
import NovaSidebarWithCollapse from '../components/NovaSidebarWithCollapse';
import NovaAvatarImg, { novaAvatarChapterFromSlug } from '../components/NovaAvatarImg';
import { useApp } from '../context/AppContext';
import { demoSectionCardStackStyle, DEMO_PRODUCT_INPUT_SCOPE_CLASS } from '../designSystem';
import './WomenLikeMe.fertility.css';

const FONT_SERIF = "'Playfair Display', Georgia, serif";
const FONT_BODY = "'Plus Jakarta Sans', -apple-system, sans-serif";

const filterPills = ['All matches', 'Same stim day', 'Same clinic', 'Low AMH', 'Near me', 'IVF cycle 2+'];

const matches = [
  { id: 1, initials: 'JM', av: 'pink', name: 'Jasmine M.', matchPct: 97, detail: 'IVF · Cycle 2 · Stim day 8 · Antagonist · Columbus, OH', chips: ['Same stim day', 'Same clinic', 'Low AMH'], quote: 'Also doing stim day 8 scans tomorrow. Nervous but ready — anyone else?', isFeatured: true, hasMessage: true },
  { id: 2, initials: 'NR', av: 'sage', name: 'Noelle R.', matchPct: 91, detail: 'Stims day 8 · Antagonist · Cleveland, OH · Low AMH', chips: ['Same stim day', 'Low AMH'], hasMessage: false },
  { id: 3, initials: 'AK', av: 'amber', name: 'Ava K.', matchPct: 88, detail: 'Transfer prep · Cincinnati, OH · Cycle 2', chips: ['Same clinic', 'Cycle 2+'], hasMessage: true },
  { id: 4, initials: 'SL', av: 'purple', name: 'Sofia L.', matchPct: 84, detail: 'Postpartum · 6 weeks · IVF · Low AMH', chips: ['Low AMH', 'Completed IVF'], hasMessage: false },
  { id: 5, initials: 'ET', av: 'blue', name: 'Emma T.', matchPct: 81, detail: 'Pregnancy · Week 18 · IVF cycle 2 · Low AMH', chips: ['Low AMH', 'Cycle 2+'], hasMessage: false },
];

const connectedWomen = [
  { initials: 'JM', av: 'pink', name: 'Jasmine M.', sub: 'Online now · IVF · Cycle 2 · Columbus', online: true },
  { initials: 'AK', av: 'amber', name: 'Ava K.', sub: 'Online now · Transfer prep · Cincinnati', online: true },
];

const rooms = [
  { name: 'Stims support', desc: 'Daily bloating, monitoring results, injection tips', online: 7, hot: true },
  { name: 'Transfer & TWW', desc: 'Embryo transfer prep · two-week wait support', online: 9, hot: false },
  { name: 'Trigger shot night', desc: 'Timing, anxiety, what to expect after', online: 4, hot: false },
  { name: 'Retrieval recovery', desc: "Pain management, rest, what's normal", online: 6, hot: false },
  { name: 'Beta day', desc: 'Waiting for results together', online: 3, hot: false, lowActivity: true },
  { name: 'Recurrent loss support', desc: 'Safe space for women navigating pregnancy loss', online: 5, hot: false },
];

const myGroups = [
  { icon: '🌸', iconBg: 'pink', name: 'IVF Warriors — Cycle 1', meta: '3,240 members · your cycle', joined: true },
  { icon: '🌱', iconBg: 'sage', name: 'Due in Summer 2026', meta: '1,890 members', joined: true },
];

const suggestedGroups = [
  { icon: '💜', iconBg: 'purple', name: 'Low AMH Warriors', meta: '2,100 members · Nova matched you', match: true },
  { icon: '🧡', iconBg: 'pink', name: 'PCOS Sisters', meta: '4,100 members · Nova matched you', match: true },
  { icon: '🌼', iconBg: 'amber', name: 'Fertility & Mental Health', meta: '1,890 members', match: false },
  { icon: '🕊️', iconBg: 'purple', name: 'Recurrent Loss & Hope', meta: '1,240 members · Nova matched you', match: true },
  { icon: '👩‍👩‍👧', iconBg: 'sage', name: 'LGBTQ+ Family Building', meta: '2,870 members', match: false },
  { icon: '🌟', iconBg: 'amber', name: 'Fertility Over 40', meta: '3,100 members', match: false },
  { icon: '🤍', iconBg: 'pink', name: 'Single Moms by Choice', meta: '980 members', match: false },
  { icon: '🔬', iconBg: 'purple', name: 'Donor Egg Journey', meta: '1,560 members', match: false },
];

const avStyles = {
  pink: { bg: '#F4C0D1', color: '#72243E' },
  sage: { bg: '#9FE1CB', color: '#0F6E56' },
  amber: { bg: '#f5c97a', color: '#be7a1a' },
  purple: { bg: '#afa9ec', color: '#534ab7' },
  blue: { bg: '#b5d4f4', color: '#185fa5' },
};

const iconBgStyles = { pink: '#FBEAF0', sage: '#E1F5EE', purple: '#EEEDFE', amber: '#fdf3e0' };

function FertilityConnectDemo({ navigate }) {
  const seeAll = () => navigate('/signup');

  function DiscoverHeader({ eyebrow, title, sub }) {
    return (
      <div className="fec-discover-header">
        <div className="fec-discover-header__left">
          <div className="fec-discover-eyebrow">{eyebrow}</div>
          <h2 className="fec-discover-title">{title}</h2>
          <p className="fec-discover-sub">{sub}</p>
        </div>
        <button type="button" className="fec-discover-see-all" onClick={seeAll}>
          See all →
        </button>
      </div>
    );
  }

  function NovaRecStamp() {
    return (
      <div className="fec-nova-rec-stamp">
        <span className="fec-nova-rec-stamp__avatar" aria-hidden>
          N
        </span>
        <span>Nova recommended</span>
      </div>
    );
  }

  return (
    <div className="fec-connect-root chapter-fertility">
      <div className="fec-connect-layout">
        <div className="fec-connect-main">
          <div className="page-header-block" style={{ marginBottom: 16 }}>
            <div className="page-title-card">
              <div className="page-eyebrow">🌸 Fertility · Connect</div>
              <div className="page-title-card__main">
                <div>
                  <h1 className="page-title">Find your people</h1>
                  <p className="page-descriptor">
                    Nova matches you to the right women, rooms, and groups — at your exact stage.
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                  <span className="page-badge">Cycle 2 · Stim day 8</span>
                  <button className="page-cta" type="button" onClick={() => navigate('/signup')}>
                    Join the waitlist
                  </button>
                </div>
              </div>
              <div className="page-pills">
                <span className="fec-status-chip">
                  <span className="fec-status-chip__dot" />
                  38 women online now
                </span>
                <span className="fec-status-chip">
                  <span className="fec-status-chip__dot" />
                  3 rooms live
                </span>
                <span className="fec-status-chip fec-status-chip--plain">Cycle 2 · Stim day 8</span>
              </div>
            </div>
          </div>

          <div className="fec-connect-discover">
            {/* Section 1 — Nova Connections */}
            <section className="fec-connect-section fec-connect-section--discover">
              <DiscoverHeader
                eyebrow="Nova Connections"
                title="Women like you"
                sub="Matched to your cycle, chapter, and location by Nova."
              />
              <div className="fec-person-discover-grid">
                <div className="fec-person-card fec-person-card--top fec-person-card--discover">
                  <div className="fec-person-card__nova-top">
                    <div className="fec-person-card__discover-row">
                      <div className="fec-person-card__avatar fec-av-disc-jm">JM</div>
                      <div className="fec-person-card__discover-info">
                        <div className="fec-person-disc-name-row">
                          <span className="fec-person-disc-name">Jasmine M.</span>
                          <span className="fec-person-disc-pct">97%</span>
                        </div>
                      <div className="fec-person-disc-meta">IVF · Cycle 2 · Stim day 8 · Columbus</div>
                      <div className="fec-tags fec-tags--discover">
                        {['Same stim day', 'Same clinic', 'Low AMH'].map((c) => (
                          <span key={c} className="fec-tag fec-tag--discover">
                            {c}
                          </span>
                        ))}
                      </div>
                        <div className="fec-disc-btn-row">
                          <button type="button" className="fec-disc-btn-connect" onClick={() => navigate('/signup')}>
                            Connect
                          </button>
                          <button type="button" className="fec-disc-btn-message" onClick={() => navigate('/signup')}>
                            Message
                          </button>
                        </div>
                      </div>
                    </div>
                    <NovaRecStamp />
                  </div>
                </div>

                <div className="fec-person-card fec-person-card--top fec-person-card--discover">
                  <div className="fec-person-card__nova-top">
                    <div className="fec-person-card__discover-row">
                      <div className="fec-person-card__avatar fec-av-disc-nr">NR</div>
                      <div className="fec-person-card__discover-info">
                        <div className="fec-person-disc-name-row">
                          <span className="fec-person-disc-name">Noelle R.</span>
                          <span className="fec-person-disc-pct">91%</span>
                        </div>
                      <div className="fec-person-disc-meta">Stim day 8 · Antagonist · Cleveland</div>
                      <div className="fec-tags fec-tags--discover">
                        {['Same stim day', 'Low AMH'].map((c) => (
                          <span key={c} className="fec-tag fec-tag--discover">
                            {c}
                          </span>
                        ))}
                      </div>
                        <div className="fec-disc-btn-row">
                          <button type="button" className="fec-disc-btn-connect" onClick={() => navigate('/signup')}>
                            Connect
                          </button>
                          <button type="button" className="fec-disc-btn-message" onClick={() => navigate('/signup')}>
                            Message
                          </button>
                        </div>
                      </div>
                    </div>
                    <NovaRecStamp />
                  </div>
                </div>

                <div className="fec-person-card fec-person-card--discover">
                  <div className="fec-person-card__discover-row">
                    <div className="fec-person-card__avatar fec-av-disc-ak">AK</div>
                    <div className="fec-person-card__discover-info">
                      <div className="fec-person-disc-name-row">
                        <span className="fec-person-disc-name">Ava K.</span>
                        <span className="fec-person-disc-pct">88%</span>
                      </div>
                      <div className="fec-person-disc-meta">Transfer prep · Cycle 2 · Cincinnati</div>
                      <div className="fec-tags fec-tags--discover">
                        {['Same clinic', 'Cycle 2+'].map((c) => (
                          <span key={c} className="fec-tag fec-tag--discover">
                            {c}
                          </span>
                        ))}
                      </div>
                      <div className="fec-disc-btn-row">
                        <button type="button" className="fec-disc-btn-connect" onClick={() => navigate('/signup')}>
                          Connect
                        </button>
                        <button type="button" className="fec-disc-btn-message" onClick={() => navigate('/signup')}>
                          Message
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="fec-person-card fec-person-card--discover">
                  <div className="fec-person-card__discover-row">
                    <div className="fec-person-card__avatar fec-av-disc-sl">SL</div>
                    <div className="fec-person-card__discover-info">
                      <div className="fec-person-disc-name-row">
                        <span className="fec-person-disc-name">Sofia L.</span>
                        <span className="fec-person-disc-pct">84%</span>
                      </div>
                      <div className="fec-person-disc-meta">Postpartum · IVF · Low AMH</div>
                      <div className="fec-tags fec-tags--discover">
                        {['Low AMH', 'Completed IVF'].map((c) => (
                          <span key={c} className="fec-tag fec-tag--discover">
                            {c}
                          </span>
                        ))}
                      </div>
                      <div className="fec-disc-btn-row">
                        <button type="button" className="fec-disc-btn-connect" onClick={() => navigate('/signup')}>
                          Connect
                        </button>
                        <button type="button" className="fec-disc-btn-message" onClick={() => navigate('/signup')}>
                          Message
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 2 — Live rooms (single card, 3-column grid) */}
            <section className="fec-connect-section fec-connect-section--discover fec-live-rooms-merged">
              <DiscoverHeader
                eyebrow="Nova Live Rooms"
                title="Support happening right now"
                sub="Open any time, expert-led sessions, and local rooms — all in one place."
              />
              <div className="fec-live-rooms-grid">
                <div className="fec-live-rooms-grid__col">
                  <div className="fec-live-rooms-grid__head">
                    <div className="fec-live-rooms-grid__title">Open 24/7 — drop in any time</div>
                    <div className="fec-live-rooms-grid__meta">
                      These rooms never close. Day or night, mid-spiral, post-scan.
                    </div>
                  </div>
                  <div className="fec-discover-rooms">
                    <div className="fec-room-row">
                      <div className="fec-room-row__left">
                        <div className="fec-room-emoji-box" aria-hidden>
                          💉
                        </div>
                        <div>
                          <div className="fec-room-card__title">Stims support</div>
                          <div className="fec-room-card__sub">Monitoring results · OHSS watch · injection timing</div>
                        </div>
                      </div>
                      <div className="fec-room-row__right">
                        <span className="fec-count-text">
                          <span className="fec-count-dot" aria-hidden="true" />
                          7 in room
                        </span>
                        <button type="button" className="fec-join-now" onClick={() => navigate('/signup')}>
                          Join now
                        </button>
                      </div>
                    </div>
                    <div className="fec-room-row">
                      <div className="fec-room-row__left">
                        <div className="fec-room-emoji-box" aria-hidden>
                          ⏳
                        </div>
                        <div>
                          <div className="fec-room-card__title">Two-week wait</div>
                          <div className="fec-room-card__sub">Symptom spirals · testing day · the wait</div>
                        </div>
                      </div>
                      <div className="fec-room-row__right">
                        <span className="fec-count-text">
                          <span className="fec-count-dot" aria-hidden="true" />
                          8 in room
                        </span>
                        <button type="button" className="fec-join-now" onClick={() => navigate('/signup')}>
                          Join now
                        </button>
                      </div>
                    </div>
                    <div className="fec-room-row">
                      <div className="fec-room-row__left">
                        <div className="fec-room-emoji-box" aria-hidden>
                          💔
                        </div>
                        <div>
                          <div className="fec-room-card__title">After a loss</div>
                          <div className="fec-room-card__sub">Processing a failed cycle or negative beta — no agenda</div>
                        </div>
                      </div>
                      <div className="fec-room-row__right">
                        <span className="fec-count-text">
                          <span className="fec-count-dot" aria-hidden="true" />
                          4 in room
                        </span>
                        <button type="button" className="fec-join-now" onClick={() => navigate('/signup')}>
                          Join now
                        </button>
                      </div>
                    </div>
                    <div className="fec-room-row">
                      <div className="fec-room-row__left">
                        <div className="fec-room-emoji-box" aria-hidden>
                          🧪
                        </div>
                        <div>
                          <div className="fec-room-card__title">Beta day</div>
                          <div className="fec-room-card__sub">Waiting together · same-day nerves · no spoilers</div>
                        </div>
                      </div>
                      <div className="fec-room-row__right">
                        <span className="fec-count-text">
                          <span className="fec-count-dot" aria-hidden="true" />
                          5 in room
                        </span>
                        <button type="button" className="fec-join-now" onClick={() => navigate('/signup')}>
                          Join now
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="fec-live-rooms-grid__col">
                  <div className="fec-live-rooms-grid__head">
                    <div className="fec-live-rooms-grid__title">Your local chapter</div>
                    <div className="fec-live-rooms-grid__meta">
                      Same city, same clinics, same drive home. Local rooms grow as your community does.
                    </div>
                  </div>
                  <div className="fec-discover-rooms">
                    <div className="fec-room-row">
                      <div className="fec-room-row__left">
                        <div className="fec-room-emoji-box" aria-hidden>
                          🌸
                        </div>
                        <div>
                          <div className="fec-room-card__title">Columbus fertility chat</div>
                          <div className="fec-room-card__sub">
                            Ohio Fertility Network · local clinic recs · monitoring tips
                          </div>
                        </div>
                      </div>
                      <div className="fec-room-row__right">
                        <span className="fec-count-text">
                          <span className="fec-count-dot" aria-hidden="true" />
                          6 online
                        </span>
                        <button type="button" className="fec-join-now" onClick={() => navigate('/signup')}>
                          Join now
                        </button>
                      </div>
                    </div>
                    <div className="fec-room-row">
                      <div className="fec-room-row__left">
                        <div className="fec-room-emoji-box" aria-hidden>
                          🌸
                        </div>
                        <div>
                          <div className="fec-room-card__title">Columbus IVF support circle</div>
                          <div className="fec-room-card__sub">Shared cycles, local retrievals, central Ohio RE community</div>
                        </div>
                      </div>
                      <div className="fec-room-row__right">
                        <span className="fec-count-text">
                          <span className="fec-count-dot" aria-hidden="true" />
                          4 online
                        </span>
                        <button type="button" className="fec-join-now" onClick={() => navigate('/signup')}>
                          Join now
                        </button>
                      </div>
                    </div>
                    <div className="fec-room-row">
                      <div className="fec-room-row__left">
                        <div className="fec-room-emoji-box" aria-hidden>
                          🌸
                        </div>
                        <div>
                          <div className="fec-room-card__title">Columbus recurrent loss support</div>
                          <div className="fec-room-card__sub">
                            RPL peers · Ohio Fertility Network area · navigating loss in central Ohio together
                          </div>
                        </div>
                      </div>
                      <div className="fec-room-row__right">
                        <span className="fec-count-text">
                          <span className="fec-count-dot" aria-hidden="true" />
                          3 online
                        </span>
                        <button type="button" className="fec-join-now" onClick={() => navigate('/signup')}>
                          Join now
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="fec-live-rooms-grid__col">
                  <div className="fec-live-rooms-grid__head">
                    <div className="fec-live-rooms-grid__title">Expert-led sessions</div>
                    <div className="fec-live-rooms-grid__meta">
                      Facilitated rooms with a nurse or counselor. Capped at 20 women.
                    </div>
                  </div>
                  <div className="fec-discover-rooms">
                    <div className="fec-room-row fec-room-row--scheduled">
                      <div className="fec-room-row__left">
                        <div className="fec-room-emoji-box" aria-hidden>
                          🌙
                        </div>
                        <div>
                          <div className="fec-room-card__title">Night before retrieval · Tonight 8pm EST</div>
                          <div className="fec-room-card__sub">IVF anxiety · nurse facilitated · 14 of 20 spots filled</div>
                        </div>
                      </div>
                      <div className="fec-room-row__right fec-room-row__right--scheduled">
                        <button type="button" className="fec-join-now" onClick={() => navigate('/signup')}>
                          Register
                        </button>
                      </div>
                    </div>
                    <div className="fec-room-row fec-room-row--scheduled">
                      <div className="fec-room-row__left">
                        <div className="fec-room-emoji-box" aria-hidden>
                          💗
                        </div>
                        <div>
                          <div className="fec-room-card__title">Starting again after a failed cycle · Tomorrow 7am EST</div>
                          <div className="fec-room-card__sub">After loss · hosted · 22 registered</div>
                        </div>
                      </div>
                      <div className="fec-room-row__right fec-room-row__right--scheduled">
                        <button type="button" className="fec-join-now" onClick={() => navigate('/signup')}>
                          Register
                        </button>
                      </div>
                    </div>
                    <div className="fec-room-row fec-room-row--scheduled">
                      <div className="fec-room-row__left">
                        <div className="fec-room-emoji-box" aria-hidden>
                          🧬
                        </div>
                        <div>
                          <div className="fec-room-card__title">Low AMH — what the research shows · Fri 12pm EST</div>
                          <div className="fec-room-card__sub">RE Q&amp;A live · 8 of 20 spots left</div>
                        </div>
                      </div>
                      <div className="fec-room-row__right fec-room-row__right--scheduled">
                        <button type="button" className="fec-join-now" onClick={() => navigate('/signup')}>
                          Register
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Section 5 — Nova Spaces */}
            <section className="fec-connect-section fec-connect-section--discover">
              <DiscoverHeader
                eyebrow="Nova Spaces"
                title="Spaces for every stage"
                sub="Nova matched you to these groups based on your cycle, protocol, and stage."
              />
              <div className="fec-discover-spaces">
                <div className="fec-ns-group-row">
                  <div className="fec-room-emoji-box" aria-hidden>
                    🌸
                  </div>
                  <div className="fec-ns-group-row__text">
                    <div className="fec-ns-group-name">IVF Warriors — Cycle 1</div>
                    <div className="fec-ns-group-sub">3,240 members · your cycle · Nova active in every thread</div>
                  </div>
                  <span className="fec-ns-group-status fec-ns-group-status--joined">Joined</span>
                </div>
                <div className="fec-ns-group-row">
                  <div className="fec-room-emoji-box" aria-hidden>
                    💛
                  </div>
                  <div className="fec-ns-group-row__text">
                    <div className="fec-ns-group-name">Low AMH Warriors</div>
                    <div className="fec-ns-group-sub">2,100 members · Nova matched you · thread active now</div>
                  </div>
                  <button type="button" className="fec-ns-group-status fec-ns-group-status--join" onClick={() => navigate('/signup')}>
                    Join
                  </button>
                </div>
                <div className="fec-ns-group-row">
                  <div className="fec-room-emoji-box" aria-hidden>
                    🌙
                  </div>
                  <div className="fec-ns-group-row__text">
                    <div className="fec-ns-group-name">PCOS Sisters</div>
                    <div className="fec-ns-group-sub">4,100 members · Nova matched you</div>
                  </div>
                  <button type="button" className="fec-ns-group-status fec-ns-group-status--join" onClick={() => navigate('/signup')}>
                    Join
                  </button>
                </div>
                <div className="fec-ns-group-row">
                  <div className="fec-room-emoji-box" aria-hidden>
                    📍
                  </div>
                  <div className="fec-ns-group-row__text">
                    <div className="fec-ns-group-name">Columbus, OH fertility chat</div>
                    <div className="fec-ns-group-sub">618 members · near you</div>
                  </div>
                  <button type="button" className="fec-ns-group-status fec-ns-group-status--join" onClick={() => navigate('/signup')}>
                    Join
                  </button>
                </div>
                <div className="fec-ns-group-row">
                  <div className="fec-room-emoji-box" aria-hidden>
                    💔
                  </div>
                  <div className="fec-ns-group-row__text">
                    <div className="fec-ns-group-name">After loss — trying again</div>
                    <div className="fec-ns-group-sub">2,870 members · recurrent loss · RPL · healing</div>
                  </div>
                  <button type="button" className="fec-ns-group-status fec-ns-group-status--join" onClick={() => navigate('/signup')}>
                    Join
                  </button>
                </div>
                <div className="fec-ns-group-row">
                  <div className="fec-room-emoji-box" aria-hidden>
                    ⏳
                  </div>
                  <div className="fec-ns-group-row__text">
                    <div className="fec-ns-group-name">Two-week wait &amp; beta buddies</div>
                    <div className="fec-ns-group-sub">5,620 members · symptom spirals · testing day</div>
                  </div>
                  <button type="button" className="fec-ns-group-status fec-ns-group-status--join" onClick={() => navigate('/signup')}>
                    Join
                  </button>
                </div>
                <div className="fec-ns-group-row">
                  <div className="fec-room-emoji-box" aria-hidden>
                    💉
                  </div>
                  <div className="fec-ns-group-row__text">
                    <div className="fec-ns-group-name">Medicated cycle support</div>
                    <div className="fec-ns-group-sub">1,940 members · IUI · letrozole · injectables</div>
                  </div>
                  <button type="button" className="fec-ns-group-status fec-ns-group-status--join" onClick={() => navigate('/signup')}>
                    Join
                  </button>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function WomenLikeMe() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setNovaDraft } = useApp();
  const [activeTab, setActiveTab] = useState('wlm');
  const [filter, setFilter] = useState('All matches');

  const isDemo = location.pathname.startsWith('/demo/');
  const demoChapterSlug = useMemo(() => {
    const pathMatch = location.pathname.match(/^\/demo\/([^/]+)\//);
    return pathMatch ? pathMatch[1] : undefined;
  }, [location.pathname]);
  const novaAvatarChapter = novaAvatarChapterFromSlug(demoChapterSlug || 'fertility');
  const demoProductInputScope =
    isDemo && demoChapterSlug && ['fertility', 'ttc', 'trying-to-conceive'].includes(demoChapterSlug);

  const isFertilityConnectDemo = isDemo && demoChapterSlug === 'fertility';

  const sl = { fontSize: 11, fontWeight: 500, color: 'var(--color-text-tertiary, #888)', marginBottom: 8 };
  const chipStyleNeutral = {
    fontSize: 10,
    fontWeight: 500,
    padding: '4px 10px',
    borderRadius: 999,
    background: 'var(--color-background-secondary, #f5f5f5)',
    border: '0.5px solid var(--color-border-tertiary, #e5e5e5)',
    color: 'var(--color-text-secondary, #666)',
  };

  return (
    <DashboardAppShell
      fontFamily={FONT_BODY}
      shellBackground={isDemo ? 'var(--sand)' : 'var(--white)'}
      mainStyle={isFertilityConnectDemo ? { background: '#F9F6F1', minHeight: '100%' } : { padding: 24 }}
      mainClassName={demoProductInputScope ? DEMO_PRODUCT_INPUT_SCOPE_CLASS : undefined}
      rightColumn={
        isDemo ? (
          <NovaSidebarWithCollapse
            navigate={navigate}
            setNovaDraft={setNovaDraft}
            chapter={demoChapterSlug}
            section={isFertilityConnectDemo ? 'connect' : undefined}
            className={demoProductInputScope ? DEMO_PRODUCT_INPUT_SCOPE_CLASS : undefined}
          />
        ) : null
      }
    >
      {isFertilityConnectDemo ? (
        <FertilityConnectDemo navigate={navigate} />
      ) : (
        <>
          {isDemo ? (
            <div className="page-header-block chapter-fertility" style={{ marginBottom: 24 }}>
              <div className="page-eyebrow">🌸 Fertility · Connect</div>
              <div className="page-title-card" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div>
                  <h1 className="page-title">Women like you</h1>
                  <p className="page-descriptor">Matched to your cycle, chapter, and location by Nova.</p>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
                  <span className="page-badge">Cycle 2 · Stim day 8</span>
                  <button className="page-cta" type="button" onClick={() => navigate('/signup')}>
                    Join the waitlist
                  </button>
                </div>
              </div>
              <div className="page-pills">
                <span className="pill-fertility">
                  <span className="pill-dot" />
                  Cycle 2 · Stim day 8
                </span>
                <span className="pill-fertility">Jamie matched this week</span>
                <span className="pill-fertility">Retrieval same week as you</span>
              </div>
            </div>
          ) : null}
          {!isDemo && (
            <div style={{ marginBottom: 24 }}>
              <h1 style={{ fontFamily: FONT_SERIF, fontSize: 24, color: 'var(--text)', marginBottom: 8 }}>Connect</h1>
              <p style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 0, lineHeight: 1.6 }}>
                Nova matches you with women at the same stage, nearby, with similar journeys.
              </p>
            </div>
          )}

          {/* Nova nudge — quiet strip */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 14px',
              borderRadius: 10,
              background: 'var(--color-background-secondary, #f5f5f5)',
              border: '0.5px solid var(--color-border-tertiary, #e5e5e5)',
              marginBottom: 16,
            }}
          >
            <NovaAvatarImg size={22} alt="" chapter={novaAvatarChapter} />
            <div style={{ flex: 1, fontSize: 12, color: 'var(--color-text-secondary, #666)', lineHeight: 1.4 }}>
              7 women on stim day 8 are in Stims Support right now — Jasmine M. is your closest match.
            </div>
            <button
              type="button"
              onClick={() => navigate('/live/stims-support')}
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: '#D4537E',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
                whiteSpace: 'nowrap',
              }}
            >
              Join →
            </button>
          </div>

          {/* Tab nav — no counts, no dots */}
          <div style={{ display: 'flex', borderBottom: '0.5px solid var(--color-border-tertiary, #e5e5e5)', marginBottom: 16, gap: 0 }}>
            {[
              { key: 'wlm', label: 'Women like me' },
              { key: 'rooms', label: 'Live rooms' },
              { key: 'groups', label: 'Groups & forums' },
            ].map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveTab(t.key)}
                style={{
                  fontFamily: FONT_BODY,
                  fontSize: 12,
                  fontWeight: 500,
                  color: activeTab === t.key ? '#D4537E' : '#888',
                  background: 'none',
                  border: 'none',
                  borderBottom: `2px solid ${activeTab === t.key ? '#D4537E' : 'transparent'}`,
                  padding: '10px 16px',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  marginBottom: -0.5,
                }}
              >
                {t.label}
              </button>
            ))}
          </div>

          {/* Women like me panel */}
          {activeTab === 'wlm' && (
            <div style={demoSectionCardStackStyle}>
              <p style={{ fontSize: 12, color: 'var(--color-text-tertiary, #888)', marginBottom: 14 }}>
                Nova matched you on stim day 8 · antagonist protocol · low AMH · near Columbus, OH
              </p>

              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 20 }}>
                {filterPills.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setFilter(p)}
                    style={{
                      fontSize: 10,
                      fontWeight: 500,
                      padding: '4px 13px',
                      borderRadius: 999,
                      border: `0.5px solid ${filter === p ? 'rgba(212,83,126,0.3)' : 'var(--border)'}`,
                      background: filter === p ? '#FBEAF0' : 'var(--sand)',
                      color: filter === p ? '#993556' : 'var(--muted)',
                      cursor: 'pointer',
                      fontFamily: FONT_BODY,
                    }}
                  >
                    {p}
                  </button>
                ))}
              </div>

              <div style={{ ...sl, marginBottom: 8 }}>Your closest match</div>
              {matches.filter((x) => x.isFeatured).map((w) => (
                <div
                  key={w.id}
                  style={{
                    background: 'var(--white)',
                    border: '0.5px solid var(--color-border-tertiary, #e5e5e5)',
                    borderRadius: 12,
                    padding: '16px 18px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <div
                      style={{
                        width: 42,
                        height: 42,
                        borderRadius: '50%',
                        background: avStyles[w.av].bg,
                        color: avStyles[w.av].color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 14,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {w.initials}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: 'var(--color-text-primary, #111)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        {w.name}
                        <span style={{ fontSize: 11, fontWeight: 500, color: '#993556' }}>{w.matchPct}%</span>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--color-text-tertiary, #888)', marginTop: 2 }}>{w.detail}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 14 }}>
                    {w.chips.map((c) => (
                      <span key={c} style={chipStyleNeutral}>
                        {c}
                      </span>
                    ))}
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      color: 'var(--color-text-secondary, #666)',
                      lineHeight: 1.65,
                      paddingLeft: 12,
                      borderLeft: '2px solid rgba(212,83,126,0.2)',
                      marginBottom: 16,
                    }}
                  >
                    {w.quote}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      onClick={() => navigate('/signup')}
                      style={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: '#fff',
                        background: '#D4537E',
                        border: 'none',
                        borderRadius: 999,
                        padding: '8px 20px',
                        cursor: 'pointer',
                      }}
                    >
                      Connect
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/signup')}
                      style={{
                        fontSize: 11,
                        fontWeight: 500,
                        color: '#D4537E',
                        background: 'transparent',
                        border: '0.5px solid rgba(212,83,126,0.3)',
                        borderRadius: 999,
                        padding: '6px 14px',
                        cursor: 'pointer',
                      }}
                    >
                      Message
                    </button>
                  </div>
                </div>
              ))}

              <div style={{ ...sl, marginBottom: 8 }}>More women like you</div>
              <div style={{ background: 'var(--white)', border: '0.5px solid var(--color-border-tertiary, #e5e5e5)', borderRadius: 12, overflow: 'hidden' }}>
                {matches
                  .filter((x) => !x.isFeatured)
                  .map((w, idx, arr) => (
                    <div
                      key={w.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '14px 18px',
                        borderBottom: idx < arr.length - 1 ? '0.5px solid var(--color-border-tertiary, #e5e5e5)' : 'none',
                        cursor: 'pointer',
                      }}
                      onClick={() => navigate('/signup')}
                      onKeyDown={() => {}}
                      role="presentation"
                    >
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          background: avStyles[w.av].bg,
                          color: avStyles[w.av].color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 12,
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        {w.initials}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 500,
                            color: 'var(--color-text-primary, #111)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                          }}
                        >
                          {w.name}
                          <span style={{ fontSize: 11, fontWeight: 500, color: '#993556' }}>{w.matchPct}%</span>
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--color-text-tertiary, #888)', marginTop: 2 }}>{w.detail}</div>
                        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 6 }}>
                          {w.chips.map((c) => (
                            <span key={c} style={chipStyleNeutral}>
                              {c}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flexShrink: 0 }}>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate('/signup');
                          }}
                          style={{
                            fontSize: 11,
                            fontWeight: 500,
                            color: '#D4537E',
                            background: 'transparent',
                            border: '0.5px solid rgba(212,83,126,0.3)',
                            borderRadius: 999,
                            padding: '5px 14px',
                            cursor: 'pointer',
                          }}
                        >
                          Connect
                        </button>
                        {w.hasMessage && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate('/signup');
                            }}
                            style={{
                              fontSize: 11,
                              fontWeight: 500,
                              color: '#D4537E',
                              background: 'transparent',
                              border: '0.5px solid rgba(212,83,126,0.3)',
                              borderRadius: 999,
                              padding: '5px 14px',
                              cursor: 'pointer',
                            }}
                          >
                            Message
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
              </div>

              <div style={{ background: 'var(--white)', border: '0.5px solid var(--color-border-tertiary, #e5e5e5)', borderRadius: 12, padding: '16px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div style={{ ...sl, marginBottom: 0 }}>Already connected</div>
                  <button type="button" onClick={() => navigate('/signup')} style={{ fontSize: 11, fontWeight: 500, color: '#D4537E', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                    Message all →
                  </button>
                </div>
                {connectedWomen.map((w, idx) => (
                  <div
                    key={w.name}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '9px 0',
                      borderBottom: idx < connectedWomen.length - 1 ? '0.5px solid var(--sand-2)' : 'none',
                    }}
                  >
                    <div
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        background: avStyles[w.av].bg,
                        color: avStyles[w.av].color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        fontWeight: 700,
                        flexShrink: 0,
                      }}
                    >
                      {w.initials}
                    </div>
                    {w.online && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#22c55e', flexShrink: 0 }} />}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>{w.name}</div>
                      <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 1 }}>{w.sub}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate('/signup')}
                      style={{
                        fontSize: 11,
                        fontWeight: 500,
                        color: '#D4537E',
                        background: 'transparent',
                        border: '0.5px solid rgba(212,83,126,0.3)',
                        borderRadius: 999,
                        padding: '6px 14px',
                        cursor: 'pointer',
                      }}
                    >
                      Message
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Live rooms panel — single vertical list */}
          {activeTab === 'rooms' && (
            <div style={demoSectionCardStackStyle}>
              <p style={{ fontSize: 12, color: 'var(--color-text-tertiary, #888)', marginBottom: 20, lineHeight: 1.5 }}>
                Immediate answers from women going through the same thing{' '}
                <strong style={{ color: 'var(--color-text-primary, #111)' }}>right now.</strong>
              </p>
              <div style={{ background: 'var(--white)', border: '0.5px solid var(--color-border-tertiary, #e5e5e5)', borderRadius: 12, overflow: 'hidden' }}>
                {rooms.map((r, idx) => (
                  <div
                    key={r.name}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '14px 18px',
                      borderBottom: idx < rooms.length - 1 ? '0.5px solid var(--color-border-tertiary, #e5e5e5)' : 'none',
                      cursor: 'pointer',
                    }}
                    onClick={() => navigate('/live/stims-support')}
                    onKeyDown={() => {}}
                    role="presentation"
                  >
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: r.lowActivity ? 'var(--color-border-secondary, #ddd)' : '#1D9E75',
                        flexShrink: 0,
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary, #111)' }}>{r.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--color-text-tertiary, #888)', marginTop: 2 }}>
                        {r.desc} · {r.online} online
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/signup');
                      }}
                      style={{
                        fontSize: 12,
                        fontWeight: 500,
                        color: r.hot ? '#fff' : '#D4537E',
                        background: r.hot ? '#D4537E' : 'transparent',
                        border: r.hot ? 'none' : '0.5px solid rgba(212,83,126,0.3)',
                        borderRadius: 999,
                        padding: '5px 16px',
                        cursor: 'pointer',
                        flexShrink: 0,
                      }}
                    >
                      Join
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Groups & forums panel */}
          {activeTab === 'groups' && (
            <div style={demoSectionCardStackStyle}>
              <div>
                <div style={{ ...sl }}>Your groups</div>
                <div style={{ background: 'var(--white)', border: '0.5px solid var(--color-border-tertiary, #e5e5e5)', borderRadius: 12, overflow: 'hidden' }}>
                  {myGroups.map((g, idx) => (
                    <div
                      key={g.name}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '14px 18px',
                        borderBottom: idx < myGroups.length - 1 ? '0.5px solid var(--color-border-tertiary, #e5e5e5)' : 'none',
                        cursor: 'pointer',
                      }}
                      onClick={() => navigate('/signup')}
                      onKeyDown={() => {}}
                      role="presentation"
                    >
                      <div
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: 10,
                          background: iconBgStyles[g.iconBg] || '#FBEAF0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 16,
                          flexShrink: 0,
                        }}
                      >
                        {g.icon}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary, #111)' }}>{g.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--color-text-tertiary, #888)', marginTop: 2 }}>{g.meta}</div>
                      </div>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 500,
                          padding: '3px 10px',
                          borderRadius: 999,
                          background: '#FBEAF0',
                          color: '#993556',
                          border: '0.5px solid rgba(212,83,126,0.2)',
                          flexShrink: 0,
                        }}
                      >
                        Joined
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ ...sl }}>Suggested for you</div>
                <div style={{ background: 'var(--white)', border: '0.5px solid var(--color-border-tertiary, #e5e5e5)', borderRadius: 12, overflow: 'hidden' }}>
                  {suggestedGroups.map((g, idx) => (
                    <div
                      key={g.name}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '14px 18px',
                        borderBottom: idx < suggestedGroups.length - 1 ? '0.5px solid var(--color-border-tertiary, #e5e5e5)' : 'none',
                        cursor: 'pointer',
                      }}
                      onClick={() => navigate('/signup')}
                      onKeyDown={() => {}}
                      role="presentation"
                    >
                      <div
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: 10,
                          background: iconBgStyles[g.iconBg] || '#FBEAF0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 16,
                          flexShrink: 0,
                        }}
                      >
                        {g.icon}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-primary, #111)' }}>{g.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--color-text-tertiary, #888)', marginTop: 2 }}>{g.meta}</div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/signup');
                        }}
                        style={{ fontSize: 12, fontWeight: 500, color: '#D4537E', background: 'none', border: 'none', cursor: 'pointer', padding: 0, flexShrink: 0 }}
                      >
                        Join →
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </DashboardAppShell>
  );
}
